import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { Multimedia } from "../../gen/witbooking/backoffice/common/common_pb";
import actions from "../../lib/actions";
import { useBackofficeClient } from "../../lib/http";
import CrudForm, { FormConfiguration } from "../shared/crud/form";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { ColorPicker } from "../shared/widgets/colorPicker";
import { getDataByActions, Image, imageManagerConfig } from "../shared/widgets/ImageManager";
import { SimpleEngineAppearanceEntity } from "./domain/engineAppearance.entity";
import {
  getSimpleEngineAppearance,
  updateSimpleEngineAppearance,
} from "./infrastructure/engineAppearance.api";
import schema from "./infrastructure/simpleEngineAppearance.schema.json";
import { engineAppearanceMessages } from "./messages";

enum MediaType {
  LOGO = "LOGO",
  MOBILE = "MOBILE",
  EMAIL = "EMAIL",
  FAVICON = "FAVICON",
}

const messages = {
  ...defaultCrudMessages,
  ...engineAppearanceMessages,
};

const sharedImageManagerConfig = {
  locales: [],
  maxFiles: 1,
  allowEdit: false,
  allowDeleteSelection: true,
  addBackgroundToPreview: true,
  formats: "png, jpeg, jpg",
};

function buildFormConfiguration(): FormConfiguration<SimpleEngineAppearanceEntity> {
  return {
    __fieldOrder: ["primaryColor", "secondaryColor", "logo", "emaillogo", "favicon", "*"],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    primaryColor: {
      fieldWidget: props => <ColorPicker withInput {...props} />,
      messages: {
        label: messages.primaryColorLabel,
        helpText: messages.primaryColorHelpText,
      },
    },
    secondaryColor: {
      fieldWidget: props => <ColorPicker withInput {...props} />,
      messages: {
        label: messages.secondaryColorLabel,
        helpText: messages.secondaryColorHelpText,
      },
    },
    logo: {
      ...imageManagerConfig({
        ...sharedImageManagerConfig,
        helpText: messages.logoHelpText,
      }),
      messages: {
        label: messages.logo,
      },
    },
    emaillogo: {
      ...imageManagerConfig({
        ...sharedImageManagerConfig,
        helpText: messages.logoHelpText,
      }),
      messages: {
        label: messages.emailLogo,
      },
    },
    favicon: {
      ...imageManagerConfig({
        ...sharedImageManagerConfig,
        formats: "ico",
        helpText: messages.faviconHelpText,
      }),
      messages: {
        label: messages.favicon,
      },
    },
  };
}

function EngineAppearanceForm({ propertyTicker }: { propertyTicker: string }) {
  const intl = useIntl();
  const [engineAppearance, setEngineAppearance] = useState<
    SimpleEngineAppearanceEntity | undefined
  >(undefined);
  const handleGetOne = useCallback(async () => {
    const simpleEngineAppearance = await getSimpleEngineAppearance();
    setEngineAppearance(simpleEngineAppearance);
    return simpleEngineAppearance;
  }, []);
  const httpClient = useBackofficeClient();

  const fetchCreateImage = useCallback(
    async (mediaType: MediaType, data: Image) => {
      const formData = new FormData();
      formData.append("image", data);
      formData.append("id", data.id);
      formData.append("attribute", mediaType);
      await httpClient.patch<FormData, Multimedia.AsObject>(
        `/media/v2/${propertyTicker}/propertyConfiguration`,
        formData
      );
    },
    [httpClient, propertyTicker]
  );
  const fetchUploadImages = useCallback(
    async ({
      entity,
      mediaType,
    }: {
      entity: SimpleEngineAppearanceEntity;
      mediaType: MediaType;
    }) => {
      const currentEngineAppearance = engineAppearance;
      if (!currentEngineAppearance) {
        return;
      }
      let createImg: Image[] | undefined = undefined;
      switch (mediaType) {
        case MediaType.LOGO:
          createImg = getDataByActions(currentEngineAppearance.logo, entity.logo).create;
          break;
        case MediaType.EMAIL:
          createImg = getDataByActions(currentEngineAppearance.emaillogo, entity.emaillogo).create;
          break;
        case MediaType.FAVICON:
          createImg = getDataByActions(currentEngineAppearance.favicon, entity.favicon).create;
          break;
        default:
          return;
      }
      if (!createImg || !createImg[0]) {
        return;
      }
      return await fetchCreateImage(mediaType, createImg[0]);
    },
    [engineAppearance, fetchCreateImage]
  );

  const handleUpdate: (entity: SimpleEngineAppearanceEntity) => Promise<void> = useCallback(
    async entity => {
      await updateSimpleEngineAppearance(entity);
      await fetchUploadImages({ entity, mediaType: MediaType.LOGO });
      await fetchUploadImages({ entity, mediaType: MediaType.EMAIL });
      await fetchUploadImages({ entity, mediaType: MediaType.FAVICON });
      alert(intl.formatMessage(messages.saveSuccess));
      return;
    },
    [fetchUploadImages, intl]
  );
  const formConfiguration = useMemo(() => buildFormConfiguration(), []);

  const validate = useCallback(
    (formData: SimpleEngineAppearanceEntity, errors) => {
      if (!formData.logo || formData.logo.length < 1) {
        errors.logo.addError(intl.formatMessage(messages.requiredImages));
      }
      if (!formData.emaillogo || formData.emaillogo.length < 1) {
        errors.emaillogo.addError(intl.formatMessage(messages.requiredImages));
      }
      if (!formData.favicon || formData.favicon.length < 1) {
        errors.favicon.addError(intl.formatMessage(messages.requiredImages));
      }
      return errors;
    },
    [intl]
  );

  return (
    <CrudForm<SimpleEngineAppearanceEntity>
      id={"engineAppearance"}
      schema={schema}
      formConfiguration={formConfiguration}
      messages={messages}
      onGet={handleGetOne}
      onUpdate={handleUpdate}
      canUpdate={actions.ModifyAppeareanceSettings}
      enumsOptional
      validate={validate}
    />
  );
}

export default withAuthorizationVerifier(EngineAppearanceForm);
